"use client";

import { SearchDialog, type SearchDialogGroup } from "@/components/search-dialog";
import { Button } from "@/components/ui/button";
import { CopyIcon } from "@/components/ui/copy";
import { SquarePenIcon } from "@/components/ui/square-pen";
import type { DbAgent, DbTeam } from "@/types/db";
import { BotIcon } from "lucide-react";
import { toast } from "sonner";

const ELEVATED_ROLES = ["team_owner", "admin", "superadmin"];

const UNASSIGNED_TEAM_ID = "__unassigned__";

export type AgentDialogSelection = {
  agent: DbAgent;
  teamId: string | null;
};

type AgentDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  agents: DbAgent[];
  teams: DbTeam[];
  currentUserId?: string | null;
  onSelect: (selection: AgentDialogSelection) => void;
  onCreate?: () => void;
  onEdit?: (agent: DbAgent) => void;
  onLearn?: () => void;
};

export function AgentDialog({
  open,
  onOpenChange,
  agents,
  teams,
  currentUserId,
  onSelect,
  onCreate,
  onEdit,
  onLearn,
}: AgentDialogProps) {
  const teamOrder = new Map(
    teams.map((team, index) => [team.id, index])
  );

  // Map of team ID → current user's role in that team.
  const teamRoleById = new Map(
    teams.map((team) => [team.id, team.currentUserRole ?? null])
  );

  const groupsByTeam = new Map<
    string,
    {
      heading: string;
      sortOrder: number;
      items: SearchDialogGroup["items"];
    }
  >();

  for (const agent of agents) {
    // Owner can always edit. Elevated members (team_owner/admin/superadmin)
    // in any of the agent's teams can also edit.
    const canEdit = Boolean(
      onEdit &&
        ((currentUserId && agent.user_id === currentUserId) ||
          agent.teams.some((t) => {
            const role = teamRoleById.get(t.id);
            return role != null && ELEVATED_ROLES.includes(role);
          }))
    );

    const memberships =
      agent.teams.length > 0
        ? agent.teams
        : [
            {
              id: UNASSIGNED_TEAM_ID,
              name: "Unassigned",
              is_home: false,
            },
          ];

    for (const team of memberships) {
      const existing = groupsByTeam.get(team.id) ?? {
        heading: team.name,
        sortOrder:
          teamOrder.get(team.id) ??
          (team.id === UNASSIGNED_TEAM_ID
            ? teams.length + 1
            : teams.length),
        items: [],
      };

      existing.items.push({
        id: `${team.id}::${agent.id}`,
        value: [team.name, agent.name]
          .filter(Boolean)
          .join(" "),
        label: agent.name,
        icon: <BotIcon className="size-4 shrink-0 text-muted-foreground" />,
        meta: (
          <span className="ml-auto flex min-w-0 items-center gap-2">
            <Button
              aria-label={`Copy link to ${agent.name}`}
              className="h-6 w-6 p-0 text-muted-foreground"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                const url = `${window.location.origin}/?agent=${agent.id}`;
                navigator.clipboard.writeText(url).then(() => {
                  toast.success("Copied to clipboard");
                }).catch(() => {});
              }}
              onMouseDown={(event) => {
                // Prevent cmdk from handling this as item selection.
                event.preventDefault();
                event.stopPropagation();
              }}
              size="icon"
              type="button"
              variant="ghost"
            >
              <CopyIcon size={14} />
            </Button>
            {canEdit ? (
              <Button
                aria-label={`Edit agent ${agent.name}`}
                className="h-6 w-6 p-0 text-muted-foreground"
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  onEdit?.(agent);
                }}
                onMouseDown={(event) => {
                  // Prevent cmdk from handling this as item selection.
                  event.preventDefault();
                  event.stopPropagation();
                }}
                size="icon"
                type="button"
                variant="ghost"
              >
                <SquarePenIcon size={14} />
              </Button>
            ) : null}
          </span>
        ),
      });

      groupsByTeam.set(team.id, existing);
    }
  }

  const groups: SearchDialogGroup[] = Array.from(groupsByTeam.values())
    .sort(
      (a, b) =>
        a.sortOrder - b.sortOrder || a.heading.localeCompare(b.heading)
    )
    .map(({ heading, items }) => ({
      heading,
      items: items.sort((a, b) => a.label.localeCompare(b.label)),
    }));

  const handleSelect = (id: string) => {
    const [teamId, agentId] = id.split("::");
    const agent = agents.find((item) => item.id === agentId);

    if (agent) {
      onSelect({
        agent,
        teamId:
          teamId === UNASSIGNED_TEAM_ID ? null : teamId,
      });
    }
  };

  return (
    <SearchDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Activate agent"
      description="Choose an agent to start a chat with"
      placeholder="Search agents…"
      emptyMessage="No agents found."
      groups={groups}
      onSelect={handleSelect}
      onCreate={onCreate}
      createLabel="Agent"
      onLearn={onLearn}
      learnLabel="Learn about agents"
    />
  );
}